import { VariantProps, cva } from 'class-variance-authority';
import { ChangeEvent, DetailedHTMLProps, FC, ForwardedRef, InputHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Checkbox.module.scss';

const checkboxVariants = cva(
    [styles['checkbox'], 'bg-transparent', 'border border-grayscale-300', 'align-top cursor-pointer outline-none'],
    {
        variants: {
            size: {
                sm: ['w-3 h-3 rounded'],
                normal: ['w-4 h-4 rounded'],
                lg: ['w-5 h-5 rounded'],
            },
            color: {
                primary: ['checked:bg-primary checked:border-primary'],
                secondary: ['checked:bg-secondary checked:border-secondary'],
                success: ['checked:bg-success-500 checked:border-success-500'],
                danger: ['checked:bg-danger-500 checked:border-danger-500'],
                warning: ['checked:bg-warning-500 checked:border-warning-500'],
            },
            disabled: {
                true: ['bg-grayscale-100 opacity-60'],
                false: [],
            },
        },
        defaultVariants: {
            size: 'normal',
            disabled: false,
            color: 'primary',
        },
    }
);

export interface CheckboxProps
    extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size' | 'color'>,
        VariantProps<typeof checkboxVariants> {
    ref?: ForwardedRef<HTMLInputElement>;
    onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
    const { size, color, disabled, className, onCheckedChange, onChange, ...rest } = props;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);

        onCheckedChange?.(e.target.checked);
    };

    return (
        <input
            {...rest}
            ref={ref}
            type="checkbox"
            disabled={disabled}
            onChange={handleChange}
            className={twMerge(checkboxVariants({ size, color, disabled, className }))}
        />
    );
});

export default Checkbox;
